import React from 'react'
import { Modal, Button, Space, Typography, Divider, Image, Tooltip, message as antMessage, List, Avatar, Popconfirm, Spin } from 'antd'
import {
    EditOutlined,
    CopyOutlined,
    PlayCircleOutlined,
    DeleteOutlined,
    UserOutlined
} from '@ant-design/icons'
import { useGetCommentsByContentQuery, useDeleteCommentMutation, IComment } from '../redux/api/commentApi'

const { Title, Text, Paragraph } = Typography

interface ContentItem {
    id: string
    title: string
    description: string
    category: string
    thumbnail: string
    url: string
    createdAt?: string
    likes?: number
}

interface ViewContentModalProps {
    isVisible: boolean
    onClose: () => void
    onEdit: () => void
    type: 'video' | 'audio' | 'book'
    content: ContentItem | null
}

export const ViewContentModal: React.FC<ViewContentModalProps> = ({
    isVisible,
    onClose,
    onEdit,
    type,
    content,
}) => {
    // Fetch comments
    const { data: commentsData, isLoading: commentsLoading } = useGetCommentsByContentQuery(
        { contentType: type, contentId: content?.id || '' },
        { skip: !isVisible || !content?.id }
    )

    // Delete mutation
    const [deleteComment, { isLoading: isDeleting }] = useDeleteCommentMutation()

    const handleDeleteComment = async (commentId: string) => {
        try {
            await deleteComment(commentId).unwrap()
            antMessage.success('Comment deleted successfully')
        } catch (error) {
            antMessage.error('Failed to delete comment')
        }
    }

    const handleCopyUrl = () => {
        if (content?.url) {
            navigator.clipboard.writeText(content.url)
            antMessage.success('URL copied to clipboard')
        }
    }

    const getTypeLabel = () => {
        switch (type) {
            case 'video':
                return 'Video'
            case 'audio':
                return 'Audio'
            case 'book':
                return 'Book (PDF)'
            default:
                return 'Content'
        }
    }

    const renderMediaPreview = () => {
        if (!content?.url) {
            return (
                <div
                    style={{
                        padding: '40px',
                        textAlign: 'center',
                        background: '#f5f5f5',
                        borderRadius: '8px',
                    }}
                >
                    <Text type="secondary">No media file available</Text>
                </div>
            )
        }

        switch (type) {
            case 'video':
                return (
                    <div style={{ borderRadius: '8px', overflow: 'hidden', background: '#000' }}>
                        <video
                            controls
                            src={content.url}
                            poster={content.thumbnail}
                            style={{
                                width: '100%',
                                maxHeight: '400px',
                                display: 'block',
                            }}
                        >
                            Your browser does not support the video tag.
                        </video>
                    </div>
                )

            case 'audio':
                return (
                    <div
                        style={{
                            borderRadius: '8px',
                            overflow: 'hidden',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            padding: '24px',
                        }}
                    >
                        {content.thumbnail && (
                            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                                <Image
                                    src={content.thumbnail}
                                    alt={content.title}
                                    style={{
                                        maxWidth: '200px',
                                        maxHeight: '200px',
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                    }}
                                    preview={false}
                                />
                            </div>
                        )}
                        <audio
                            controls
                            src={content.url}
                            style={{
                                width: '100%',
                                display: 'block',
                            }}
                        >
                            Your browser does not support the audio element.
                        </audio>
                    </div>
                )

            case 'book':
                return (
                    <div
                        style={{
                            borderRadius: '8px',
                            overflow: 'hidden',
                            border: '1px solid #d9d9d9',
                        }}
                    >
                        <embed
                            src={content.url}
                            type="application/pdf"
                            width="100%"
                            height="500px"
                            style={{ display: 'block' }}
                        />
                    </div>
                )

            default:
                return null
        }
    }

    return (
        <Modal
            title={
                <Space>
                    <PlayCircleOutlined />
                    <span>Preview {getTypeLabel()}</span>
                </Space>
            }
            open={isVisible}
            onCancel={onClose}
            width={800}
            footer={[
                <Button key="copy" icon={<CopyOutlined />} onClick={handleCopyUrl}>
                    Copy URL
                </Button>,
                <Button key="edit" icon={<EditOutlined />} onClick={onEdit}>
                    Edit
                </Button>,
                <Button key="close" type="primary" onClick={onClose}>
                    Close
                </Button>,
            ]}
        >
            {/* Media Preview */}
            <div style={{ marginBottom: '24px' }}>{renderMediaPreview()}</div>

            <Divider />

            {/* Content Details */}
            <div>
                <Title level={4} style={{ marginBottom: '8px' }}>
                    {content?.title || 'Untitled'}
                </Title>

                <Space style={{ marginBottom: '16px' }}>
                    <Text
                        style={{
                            background: '#f0f5ff',
                            padding: '4px 12px',
                            borderRadius: '4px',
                            color: '#1890ff',
                        }}
                    >
                        {content?.category || 'Uncategorized'}
                    </Text>
                    {content?.likes !== undefined && (
                        <Text type="secondary">❤️ {content.likes} likes</Text>
                    )}
                </Space>

                <Paragraph
                    ellipsis={{ rows: 3, expandable: true, symbol: 'more' }}
                    style={{ color: '#666' }}
                >
                    {content?.description || 'No description available'}
                </Paragraph>

                {content?.url && (
                    <div style={{ marginTop: '16px' }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            File URL:{' '}
                            <Tooltip title={content.url}>
                                <Text code style={{ fontSize: '11px' }}>
                                    {content.url.length > 60
                                        ? `${content.url.substring(0, 60)}...`
                                        : content.url}
                                </Text>
                            </Tooltip>
                        </Text>
                    </div>
                )}
            </div>

            <Divider />

            {/* Comments Section */}
            <div style={{ marginTop: '24px' }}>
                <Title level={5}>Comments ({commentsData?.count || 0})</Title>

                {commentsLoading ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <Spin />
                    </div>
                ) : (
                    <List
                        className="comment-list"
                        itemLayout="horizontal"
                        dataSource={commentsData?.comments || []}
                        renderItem={(item: IComment) => (
                            <List.Item
                                actions={[
                                    <Popconfirm
                                        title="Delete this comment?"
                                        onConfirm={() => handleDeleteComment(item.commentId)}
                                        okText="Yes"
                                        cancelText="No"
                                    >
                                        <Button
                                            type="text"
                                            danger
                                            icon={<DeleteOutlined />}
                                            loading={isDeleting}
                                        />
                                    </Popconfirm>
                                ]}
                            >
                                <List.Item.Meta
                                    avatar={<Avatar src={item.userProfilePicture} icon={<UserOutlined />} />}
                                    title={
                                        <Space>
                                            <Text strong>{item.userName}</Text>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                {new Date(item.createdAt).toLocaleDateString()}
                                            </Text>
                                        </Space>
                                    }
                                    description={
                                        <div>
                                            <div style={{ color: '#333' }}>{item.comment}</div>
                                            <div style={{ fontSize: '12px', marginTop: '4px' }}>❤️ {item.likes}</div>
                                        </div>
                                    }
                                />
                            </List.Item>
                        )}
                        locale={{ emptyText: 'No comments yet' }}
                        style={{ maxHeight: '300px', overflowY: 'auto' }}
                    />
                )}
            </div>
        </Modal>
    )
}
